import React, { useEffect, useMemo, useCallback, memo } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useOrganizations } from 'data/repositories/organizationsRepository';
import { closeModal } from 'data/reducers/modalReducer';
import { showToast } from 'data/reducers/toastReducer';
import StandardModal from '../StandardModal';
import { FormInput } from 'components/Form';

const AddOrganizationModal = memo(({ options }) => {
	const dispatch = useDispatch();
	const {
		actions,
		isCreating,
		isUpdating,
		createError,
		updateError,
		selectedItem,
	} = useOrganizations();

	const isEditMode = !!options?.organizationId;
	const organizationId = options?.organizationId;
	const onSuccess = options?.onSuccess;

	// React Hook Form
	const { control, handleSubmit, reset, formState } = useForm({
		defaultValues: {
			name: '',
			description: '',
		},
		mode: 'onChange',
	});

	// Load organization data if editing
	useEffect(() => {
		if (isEditMode && organizationId) {
			actions.fetchOne(organizationId);
		}
	}, [isEditMode, organizationId, actions]);

	// Populate form when data loads
	useEffect(() => {
		if (isEditMode && selectedItem) {
			reset({
				name: selectedItem.name || '',
				description: selectedItem.description || '',
			});
		}
	}, [isEditMode, selectedItem, reset]);

	// Memoized error
	const currentError = useMemo(() => {
		return isEditMode ? updateError : createError;
	}, [isEditMode, updateError, createError]);

	// Memoized loading state
	const isLoading = useMemo(() => {
		return isEditMode ? isUpdating : isCreating;
	}, [isEditMode, isUpdating, isCreating]);

	// Form submit handler
	const onSubmit = useCallback(
		async (data) => {
			try {
				if (isEditMode) {
					await actions.update(organizationId, data);
					dispatch(
						showToast({
							title: 'Success',
							body: 'Organization updated successfully',
							type: 'success',
						}),
					);
				} else {
					await actions.create(data);
					dispatch(
						showToast({
							title: 'Success',
							body: 'Organization created successfully',
							type: 'success',
						}),
					);
				}

				if (onSuccess) {
					onSuccess();
				}

				dispatch(closeModal());
			} catch (error) {
				console.error('Error saving organization:', error);
			}
		},
		[isEditMode, organizationId, actions, dispatch, onSuccess],
	);

	const handleCancel = useCallback(() => {
		dispatch(closeModal());
	}, [dispatch]);

	return (
		<StandardModal
			title={isEditMode ? 'Edit Organization' : 'Create New Organization'}
			size="lg"
			onClose={handleCancel}
			noFooter
		>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="modal-body">
					{currentError && (
						<div className="alert alert-danger mb-4">
							<i className="bi bi-exclamation-triangle me-2"></i>
							{typeof currentError === 'string'
								? currentError
								: currentError.message || 'An error occurred'}
						</div>
					)}

					<FormInput
						name="name"
						control={control}
						label="Organization Name"
						placeholder="Enter organization name or leave blank for default"
						maxLength={100}
						helperText="If left blank, your organization will be named based on your account name"
					/>

					<FormInput
						name="description"
						control={control}
						label="Description (Optional)"
						placeholder="Enter a description for this organization"
						maxLength={500}
						as="textarea"
						rows={3}
					/>

					{!isEditMode && (
						<div className="alert alert-info mt-3">
							<i className="bi bi-info-circle me-2"></i>
							<strong>Note:</strong> You can add members to this organization
							after creating it.
						</div>
					)}
				</div>

				<div className="modal-footer">
					<button
						type="button"
						className="btn btn-secondary"
						onClick={handleCancel}
						disabled={isLoading}
					>
						Cancel
					</button>
					<button
						type="submit"
						className="btn btn-primary"
						disabled={isLoading || !formState.isValid}
					>
						{isLoading ? (
							<>
								<span
									className="spinner-border spinner-border-sm me-2"
									role="status"
								/>
								{isEditMode ? 'Updating...' : 'Creating...'}
							</>
						) : (
							<>
								<i
									className={`bi ${isEditMode ? 'bi-check-lg' : 'bi-plus-lg'} me-2`}
								/>
								{isEditMode ? 'Update Organization' : 'Create Organization'}
							</>
						)}
					</button>
				</div>
			</form>
		</StandardModal>
	);
});

AddOrganizationModal.displayName = 'AddOrganizationModal';

export default AddOrganizationModal;
